import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  CheckIcon, 
  ChartBarIcon, 
  CogIcon, 
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CommandLineIcon,
  LightBulbIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SlackIcon from '../components/icons/SlackIcon';
import SalesforceIcon from '../components/icons/SalesforceIcon';
import GongIcon from '../components/icons/GongIcon';
import HubSpotIcon from '../components/icons/HubSpotIcon';
import ZendeskIcon from '../components/icons/ZendeskIcon';
import JiraIcon from '../components/icons/JiraIcon';
import TrelloIcon from '../components/icons/TrelloIcon';
import LinearIcon from '../components/icons/LinearIcon';

const LandingPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const Reveal: React.FC<{
    children: React.ReactNode;
    className?: string;
    delayMs?: number;
    eager?: boolean;
  }> = ({ children, className = '', delayMs = 0 }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      // Avoid "flash hidden" for above-the-fold content.
      const rect = el.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight) {
        setVisible(true);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    return (
      <div
        ref={ref}
        className={[
          'transition-all duration-700 ease-out will-change-transform',
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
          className,
        ].join(' ')}
        style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
      >
        {children}
      </div>
    );
  };

  // Smooth scrolling function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const features = [
    {
      icon: ChatBubbleLeftRightIcon,
      title: "AI-Powered Customer Insights",
      description: "Transform customer conversations into actionable insights with our advanced AI analysis."
    },
    {
      icon: ChartBarIcon,
      title: "Real-time Analytics",
      description: "Monitor customer health scores, MRR trends, and engagement metrics in real-time."
    },
    {
      icon: CogIcon,
      title: "Seamless Integrations",
      description: "Connect Slack, Salesforce, HubSpot, Gong, Zendesk, Jira, Trello, and Linear to unify all your customer data in one place."
    },
    {
      icon: ShieldCheckIcon,
      title: "Enterprise Security",
      description: "Multi-tenant architecture with enterprise-grade security and data isolation."
    },
    {
      icon: UserGroupIcon,
      title: "Team Collaboration",
      description: "Share insights and collaborate with your team on customer success strategies."
    },
    {
      icon: ClockIcon,
      title: "Automated Workflows",
      description: "Set up automated alerts and workflows to never miss a customer opportunity."
    }
  ];

  const integrations = [
    {
      name: "Slack",
      description: "Analyze internal conversations and customer-facing chats where real relationships are built.",
      icon: SlackIcon
    },
    {
      name: "Linear",
      description: "Bring engineering work into context with Linear issues and team activity to connect product execution to customer outcomes.",
      icon: LinearIcon,
      isNew: true
    },
    {
      name: "Gong",
      description: "Ingest call transcripts and deal intelligence to understand the context behind every strategic conversation.",
      icon: GongIcon
    },
    {
      name: "Zendesk",
      description: "Integrate support tickets to correlate customer issues with overall health and sentiment. Turn your support data into a proactive asset.",
      icon: ZendeskIcon,
      isNew: true
    },
    {
      name: "Jira",
      description: "Connect engineering work to the customer voice. Track feature requests, bug reports, and project statuses to understand how product development impacts customer satisfaction.",
      icon: JiraIcon,
      isNew: true
    },
    {
      name: "Trello",
      description: "Index Trello cards and comments so customer signals from boards, labels, and workflows become searchable insights.",
      icon: TrelloIcon,
      isNew: true
    },
    {
      name: "Salesforce",
      description: "Sync your CRM data and enrich customer profiles with conversation insights.",
      icon: SalesforceIcon
    },
    {
      name: "HubSpot",
      description: "Connect your HubSpot CRM to sync company data and customer interactions.",
      icon: HubSpotIcon
    }
  ];

  const teamMembers = [
    {
      name: "Tiffany Stelman",
      role: "CEO",
      description: "Visionary leader with 15+ years in customer success and SaaS. Previously led customer success teams at multiple high-growth companies.",
      icon: "👩‍💼"
    },
    {
      name: "Beni Levitan", 
      role: "CTO",
      description: "Technical architect and AI expert with deep experience in machine learning and software development.",
      icon: "👨‍💻"
    }
  ];

  const pricingPlans = [
    {
      name: "Free Trial",
      price: "Free",
      period: "14 days",
      description: "Perfect for small teams getting started",
      features: [
        "Up to 5 customers",
        "5 data sources",
        "Basic AI insights",
        "Slack integration only",
        "Email support"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Pilot",
      price: "$49",
      period: "per month",
      description: "Ideal for growing teams",
      features: [
        "Up to 50 customers",
        "25 data sources",
        "Advanced AI insights",
        "Slack, Salesforce, HubSpot, Zendesk, Jira, Trello & Linear",
        "Priority support",
        "Team collaboration"
      ],
      cta: "Start Pilot",
      popular: true
    },
    {
      name: "Business",
      price: "$199",
      period: "per month",
      description: "For established companies",
      features: [
        "Unlimited customers",
        "Unlimited data sources",
        "Premium AI insights",
        "All integrations + Gong",
        "Dedicated support",
        "Advanced analytics",
        "API access"
      ],
      cta: "Contact Sales",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img src="/Edyt-logo.png" alt="Edyt" className="h-8 w-8" />
              <span className="text-xl font-bold text-gray-900">Edyt</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-8">
              <SparklesIcon className="h-4 w-4 mr-2" />
              AI-Powered Customer Success Intelligence
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Transform Customer Conversations Into
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {" "}Actionable Insights
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Edyt analyzes your customer conversations across Slack, Salesforce, HubSpot, Gong, Zendesk, Jira, Trello, and Linear to provide AI-powered insights that help you prevent churn, identify expansion opportunities, and deliver exceptional customer experiences.
            </p>
            
            <div className="flex justify-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-8 py-4 text-lg">
                  Start Free Trial
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </Reveal>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for Customer Success
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to help you understand, engage, and retain your customers like never before.
            </p>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Reveal key={index} delayMs={index * 70}>
                <Card className="p-6 bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                      <feature.icon className="h-6 w-6" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Your Single Source of Customer Truth
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Edyt breaks down data silos by connecting all the tools your team already uses. Get a complete, 360-degree view of every customer interaction, from start to finish.
            </p>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {integrations.map((integration, index) => (
              <Reveal key={index} delayMs={index * 70}>
                <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200 relative">
                  {integration.isNew && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        NEW
                      </span>
                    </div>
                  )}
                  <div className="flex justify-center mb-4">
                    <integration.icon className="h-12 w-12" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {integration.name}
                  </h3>
                  <p className="text-gray-600">
                    {integration.description}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Your AI Command Center Section */}
      <section id="mcp" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 text-sm font-medium mb-8">
              <CommandLineIcon className="h-4 w-4 mr-2" />
              Powered by Model Context Protocol (MCP)
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet Your AI Command Center
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Stop navigating menus. Start getting answers. Edyt's intelligent command bar understands what you want to do and gets you there instantly.
            </p>
          </Reveal>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <Reveal delayMs={0}>
              <Card className="p-8 bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white">
                    <LightBulbIcon className="h-6 w-6" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Get Instant Insights
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"How is Acme Corp doing?"</p>
                    <p className="text-xs text-gray-500 mt-1">→ Edyt performs a RAG query across all data sources and presents a detailed analysis</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"What are the main risks for our enterprise customers?"</p>
                    <p className="text-xs text-gray-500 mt-1">→ Edyt synthesizes data to identify patterns and generate a risk summary</p>
                  </div>
                </div>
              </Card>
            </Reveal>
            
            <Reveal delayMs={70}>
              <Card className="p-8 bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                    <RocketLaunchIcon className="h-6 w-6" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Automate Your Workflows
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"Generate a QBR for Apple for last quarter."</p>
                    <p className="text-xs text-gray-500 mt-1">→ Edyt automatically creates a data-driven Quarterly Business Review, ready for your review</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"Prep me for my call with Initech."</p>
                    <p className="text-xs text-gray-500 mt-1">→ Edyt generates a pre-call briefing with key positives, emerging risks, and open questions</p>
                  </div>
                </div>
              </Card>
            </Reveal>
            
            <Reveal delayMs={140}>
              <Card className="p-8 bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white">
                    <CogIcon className="h-6 w-6" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Navigate the Platform
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"Show me all customers at risk."</p>
                    <p className="text-xs text-gray-500 mt-1">→ Edyt takes you directly to the customer dashboard with the correct filters applied</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"Take me to my settings."</p>
                    <p className="text-xs text-gray-500 mt-1">→ Instantly navigate to any page in the application</p>
                  </div>
                </div>
              </Card>
            </Reveal>
          </div>
          
          <Reveal className="text-center">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">With Edyt, your intent is the only interface you need.</h3>
              <p className="text-purple-100 mb-6">
                Simply describe what you want to accomplish, and our AI-powered command center handles the rest.
              </p>
              <Link to="/signup">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-8 py-4 text-lg">
                  Experience the Future
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Meet Our Team Section */}
      <section id="about" className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're a passionate team of engineers, designers, and customer success experts working together to build the future of customer intelligence.
            </p>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <Reveal key={index} delayMs={index * 70}>
                <Card className="p-8 bg-white/80 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-200">
                  <div className="text-center">
                    <div className="text-6xl mb-4">{member.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-lg font-semibold text-blue-600 mb-4">
                      {member.role}
                    </p>
                    <p className="text-gray-600">
                      {member.description}
                    </p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your team size and needs. All plans include our core features.
            </p>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Reveal key={index} delayMs={index * 70}>
                <Card className={`p-8 relative ${plan.popular ? 'ring-2 ring-blue-500 bg-gradient-to-br from-blue-50 to-purple-50' : 'bg-white/80 backdrop-blur-sm'} border-white/20 shadow-lg hover:shadow-xl transition-all duration-200`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600 ml-2">{plan.period}</span>
                    </div>
                    <p className="text-gray-600">{plan.description}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/signup">
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'} font-semibold shadow-lg hover:shadow-xl transition-all duration-200`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Customer Success?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join hundreds of teams already using Edyt to deliver exceptional customer experiences.
            </p>
            <div className="flex justify-center">
              <Link to="/signup">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-8 py-4 text-lg">
                  Start Your Free Trial
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Reveal>
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/Edyt-logo.png" alt="Edyt" className="h-8 w-8" />
                <span className="text-xl font-bold">Edyt</span>
              </div>
              <p className="text-gray-400">
                AI-powered customer success intelligence platform that transforms conversations into actionable insights.
              </p>
            </div>
            </Reveal>
            
            <Reveal delayMs={70}>
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors text-left">Features</button></li>
                <li><button onClick={() => scrollToSection('integrations')} className="hover:text-white transition-colors text-left">Integrations</button></li>
                <li><button onClick={() => scrollToSection('mcp')} className="hover:text-white transition-colors text-left">AI Command Center</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors text-left">Pricing</button></li>
                <li><Link to="/api" className="hover:text-white transition-colors">API</Link></li>
              </ul>
            </div>
            </Reveal>
            
            <Reveal delayMs={140}>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors text-left">About</button></li>
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            </Reveal>
            
            <Reveal delayMs={210}>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/dpa" className="hover:text-white transition-colors">DPA</Link></li>
                <li><Link to="/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link></li>
                <li><Link to="/security" className="hover:text-white transition-colors">Security</Link></li>
                <li><Link to="/compliance" className="hover:text-white transition-colors">Compliance</Link></li>
              </ul>
            </div>
            </Reveal>
          </div>
          
          <Reveal className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {currentYear} Edyt. All rights reserved.</p>
          </Reveal>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
